import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { VSLeadsFormCapture } from '@/components/vsleads/VSLeadsFormCapture';
import { PasswordCaptureEditor } from '@/components/vsleads/password-capture/PasswordCaptureEditor';
import { useToast } from "@/components/ui/use-toast";
import { VideoPlayer } from '@/components/video-player/VideoPlayer';
import { FormCaptureFields } from '@/components/vsleads/types';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';

export default function VSLeadsCreate() {
  useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<FormCaptureFields | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Extract the capture type from the query parameter
  const searchParams = new URLSearchParams(location.search);
  const captureType = searchParams.get('tipo') || 'captura-dados';
  
  // Function to handle saving the configuration
  const handleSave = async (formData: any) => {
    setIsLoading(true);
    
    try {
      // Mock API call - replace with actual API integration
      console.log('Saving strategy with data:', formData);
      
      // Add tipo to the formData
      formData.tipo = captureType;
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Show success message
      toast({
        title: translateInterfaceText("Estratégia criada com sucesso"),
        description: translateInterfaceText("Redirecionando para configuração de integrações..."),
      });
      
      // Redirect to integration page (using a mock ID for now)
      const mockId = "strategy-" + Date.now();
      navigate(`/vsleads/integracao/${mockId}`);
    } catch (error) {
      console.error('Error saving strategy:', error);
      toast({
        title: translateInterfaceText("Erro ao salvar estratégia"),
        description: translateInterfaceText("Ocorreu um erro. Tente novamente."),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle form data changes for preview
  const handleFormChange = (data: any) => {
    setFormData(data);
  };

  // Mock video features for preview
  const mockVideoFeatures = {
    videoId: formData?.videoId || '',
    videoUrl: formData?.videoId || '',
    showControls: true,
    disableControls: false,
    loop: false,
    autoPlay: false,
    showProgressBar: true
  };

  return (
    <Layout>
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex-shrink-0 bg-transparent">
          <div className="max-w-[1600px] w-full mx-auto px-4 md:px-8 pt-4 pb-4">
            <h1 className="text-[20px] md:text-[22px] font-semibold text-[#1E2330] tracking-tight leading-[1.2]">
              {captureType === 'captura-dados' ? translateInterfaceText("Criar Captura de Dados") : translateInterfaceText("Criar Captura com Senha")}
            </h1>
          </div>
          <div className="hidden lg:block h-px w-full" style={{ backgroundColor: 'rgba(0, 0, 0, 0.08)', marginTop: '5px' }} />
        </div>

        {/* Breadcrumb */}
        <div className="max-w-[1600px] w-full mx-auto px-4 md:px-8 pt-6">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/vsleads">
                  {translateInterfaceText("Captura de Leads")}</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>
                  {captureType === 'captura-dados' ? translateInterfaceText("Criar Captura de Dados") : translateInterfaceText("Criar Captura com Senha")}
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
        
        {/* Conteúdo */}
        <main className="flex-1 overflow-hidden">
          <div className="max-w-[1600px] w-full mx-auto h-full">
            <div className="px-6 py-6 h-full">
              {captureType === 'captura-senha' ? (
                <PasswordCaptureEditor isEditMode={false} />
              ) : (
                <div className="flex flex-col lg:flex-row gap-6 h-full">
                  {/* Lado esquerdo - Formulário com scroll azul */}
                  <ScrollArea className="w-full lg:w-1/2 h-full scrollbar-blue">
                    <div className="p-6">
                      <VSLeadsFormCapture
                        onSave={handleSave}
                        isSaving={isLoading}
                        initialData={formData || undefined}
                      />
                    </div>
                  </ScrollArea>

                  {/* Lado direito - Preview com scroll azul */}
                  <ScrollArea className="w-full lg:w-1/2 h-full scrollbar-blue">
                    <div className="p-6">
                      <div className="bg-muted border rounded-md p-6">
                        <h2 className="text-lg font-medium mb-4">{translateInterfaceText("Preview em Tempo Real")}</h2>
                        {formData?.videoId ? (
                          <div className="rounded-md overflow-hidden">
                            <VideoPlayer
                              features={mockVideoFeatures as any}
                              formConfig={formData}
                              isPreviewMode
                            />
                            {formData.timerEnabled && (
                              <div className="mt-4 bg-blue-50 dark:bg-blue-950 p-4 rounded-md text-sm">
                                <p className="font-medium">{translateInterfaceText("ℹ️ Formulário configurado para aparecer em:")}{" "}{formData.timerTimestamp}</p>
                                <p className="text-muted-foreground mt-1">
                                  {translateInterfaceText("Reproduza o vídeo para ver o formulário aparecendo no tempo configurado.")}</p>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="aspect-video bg-muted-foreground/10 rounded-md flex items-center justify-center">
                            <p className="text-muted-foreground">
                              {translateInterfaceText("Selecione um vídeo para visualizar a captura")}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </ScrollArea>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </Layout>
  );
}
